import { Injectable } from "@angular/core";
import * as dayjs from "dayjs";
import "dayjs/locale/it";
import "dayjs/locale/es";
import "dayjs/locale/en";
import * as customParseFormat from "dayjs/plugin/customParseFormat";
import { I18nService } from "../../i18n/service/i18n.service";
import { LanguageService } from "../language-service/language.service";
import { StringUtilsService } from "../string-utils/string-utils.service";
@Injectable({
  providedIn: "root",
})
export class DateUtilService {
  constructor(
    private i18nService: I18nService,
    private languageService: LanguageService,
    private stringUtils: StringUtilsService
  ) {
    dayjs.extend(customParseFormat);
  }
  /**
   *
   * @param dateInput Date in string format
   * @param dateFormat actual format of the date
   * @returns Date formatted based on current_date_format on language file
   */
  getDateFormatted(dateInput: string, dateFormat?: string): string {
    const currentDate = this.i18nService.getTranslation("current_date_format");
    return dayjs(dateInput, dateFormat).locale(this.getLocale()).format(currentDate);
  }

  getDateFormattedMiles(dateInput: string, dateFormat?: string): string {
    const currentDate = "YYYY-MM-DD";
    return dayjs(dateInput, dateFormat).format(currentDate);
  }

  /**
   *
   * @param dateInput Date in string format
   * @param actualFormat actual format date
   * @param newFormat desired format
   * @returns String date with new format
   */
  formatDate(
    dateInput: string,
    actualFormat: string,
    newFormat: string
  ): string {
    return dayjs(dateInput, actualFormat).locale(this.getLocale()).format(newFormat);
  }

  /**
   *
   * @param dateInput Date in string format
   * @param actualFormat actual date format
   * @returns Returns js Date object
   */
  getDateObject(dateInput: string, actualFormat: string): Date {
    const date = dayjs(dateInput, actualFormat);
    return date.toDate();
  }

  getNowFormatted(format: string): string {
    return dayjs().format(format);
  }

  getLocale(): string {
    const localeId = this.languageService.getUsersLocale();
    return localeId;
  }

  getActualMonth(): string {
    return this.stringUtils.capitalize(
      dayjs().locale(this.getLocale()).format("MMMM")
    );
  }

  getPrevMonthName(): string {
    return this.stringUtils.capitalize(
      dayjs().subtract(1, "M").locale(this.getLocale()).format("MMMM")
    );
  }
  /**
   *
   * @param first String date with format DD/MM/YYYY
   * @param second String date with format DD/MM/YYYY
   */
  sortDate(first: string, second: string): number {
    const firstDate = dayjs(first, "DD/MM/YYYY");
    const secondDate = dayjs(second, "DD/MM/YYYY");
    if (firstDate.isBefore(secondDate)) {
      return -1;
    }
    if (firstDate.isAfter(secondDate)) {
      return 1;
    }
    return 0;
  }

  isDateBeforeToday(date: string, dateFormat: string): boolean {
    const dateToCompare = dayjs(date, dateFormat);
    return dateToCompare.isBefore(dayjs());
  }

  // Verifica che la stringa rispetti ESATTAMENTE il formato atteso (parsing strict).
  isValidDate(date: string, dateFormat: string): boolean {
    return dayjs(date, dateFormat, true).isValid();
  }

  isDateAfterToday(date: string, dateFormat: string): boolean {
    const dateToCompare = dayjs(date, dateFormat);
    return dateToCompare.isAfter(dayjs());
  }

  /**
   *
   * @param firstDate Date with format DD/MM/YYYY
   * @param seconDate Date with format DD/MM/YYYY
   */
  datesDiff(
    firstDate: string,
    seconDate: string,
    unit: dayjs.QUnitType | dayjs.OpUnitType
  ): number {
    const firstFormattedDate = dayjs(firstDate, "DD/MM/YYYY");
    const secondFormattedDate = dayjs(seconDate, "DD/MM/YYYY");
    return secondFormattedDate.diff(firstFormattedDate, unit);
  }

  addMonthsToDate(date: string, months: number): string {
    const currentDate = this.i18nService.getTranslation("current_date_format");
    return (
      dayjs(date, "DD/MM/YYYY").add(months, "month").format(currentDate) ?? ""
    );
  }
}
